import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState
} from 'react';

import { getExperimentValue } from '@canvas/client-feature-flags/experiments';

import {
  computeMaxPinnedLeadingFieldCount,
  computePinnedLeadingLayout,
  type PinnedLeadingFieldLayoutItem
} from './compute-pinned-leading-layout';

export const PINNED_LEADING_URL_PARAM = 'dbPinnedLeading';

export type PinnedLeadingColumnsContextValue = {
  enabled: boolean;
  pinnedLeadingFieldCount: number;
  maxPinnedLeadingFieldCount: number;
  setPinnedLeadingFieldCount: (next: number) => void;
  layoutByFieldIndex: PinnedLeadingFieldLayoutItem[];
};

const PinnedLeadingColumnsContext =
  createContext<PinnedLeadingColumnsContextValue | null>(null);

function readPinnedCountFromUrl(): number | undefined {
  if (typeof globalThis.window === 'undefined') {
    return undefined;
  }
  try {
    const raw = new URL(globalThis.window.location.href).searchParams.get(
      PINNED_LEADING_URL_PARAM
    );
    if (raw === null || raw === '') {
      return undefined;
    }
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? n : undefined;
  } catch {
    return undefined;
  }
}

function clampPinnedLeadingFieldCount(count: number | undefined, max: number) {
  if (count === undefined || !Number.isFinite(count)) {
    return 0;
  }
  return Math.max(0, Math.min(Math.trunc(count), max));
}

export type PinnedLeadingColumnsProviderProps = {
  pinnedLeadingFieldCountFromView?: number;
  onPinnedLeadingFieldCountChange: (next: number) => void;
  fieldCount: number;
  showDragHandlerColumn: boolean;
  dragHandlerWidthPx: number;
  columnWidths: ReadonlyArray<number | undefined>;
  viewportWidthPx: number;
  children: React.ReactNode;
};

export function PinnedLeadingColumnsProvider({
  pinnedLeadingFieldCountFromView,
  onPinnedLeadingFieldCountChange,
  fieldCount,
  showDragHandlerColumn,
  dragHandlerWidthPx,
  columnWidths,
  viewportWidthPx,
  children
}: PinnedLeadingColumnsProviderProps) {
  const enabled = getExperimentValue('databasesPinnedLeadingColumns', 'enabled', {
    fireExperimentExposure: false
  });

  const [pinnedLeadingFieldCount, setPinnedLeadingFieldCountState] = useState(0);
  const appliedUrlOverrideRef = React.useRef<number | undefined>(undefined);

  const maxPinnedLeadingFieldCount = useMemo(
    () =>
      computeMaxPinnedLeadingFieldCount({
        fieldCount,
        showDragHandlerColumn,
        dragHandlerWidthPx,
        columnWidths,
        viewportWidthPx
      }),
    [columnWidths, dragHandlerWidthPx, fieldCount, showDragHandlerColumn, viewportWidthPx]
  );

  useEffect(() => {
    if (!enabled) {
      setPinnedLeadingFieldCountState(0);
      return;
    }

    const fromUrl = readPinnedCountFromUrl();
    const next = clampPinnedLeadingFieldCount(
      fromUrl ?? pinnedLeadingFieldCountFromView,
      maxPinnedLeadingFieldCount
    );

    setPinnedLeadingFieldCountState(next);

    if (
      fromUrl !== undefined &&
      appliedUrlOverrideRef.current !== next &&
      next !==
        clampPinnedLeadingFieldCount(
          pinnedLeadingFieldCountFromView,
          maxPinnedLeadingFieldCount
        )
    ) {
      appliedUrlOverrideRef.current = next;
      onPinnedLeadingFieldCountChange(next);
    }
  }, [
    enabled,
    maxPinnedLeadingFieldCount,
    onPinnedLeadingFieldCountChange,
    pinnedLeadingFieldCountFromView
  ]);

  const setPinnedLeadingFieldCount = useCallback(
    (next: number) => {
      if (!enabled) {
        return;
      }
      const clamped = clampPinnedLeadingFieldCount(next, maxPinnedLeadingFieldCount);
      setPinnedLeadingFieldCountState(clamped);
      onPinnedLeadingFieldCountChange(clamped);
    },
    [enabled, maxPinnedLeadingFieldCount, onPinnedLeadingFieldCountChange]
  );

  const layoutByFieldIndex = useMemo(() => {
    if (!enabled || fieldCount === 0) {
      return [];
    }
    return computePinnedLeadingLayout({
      pinnedLeadingFieldCount,
      fieldCount,
      showDragHandlerColumn,
      dragHandlerWidthPx,
      columnWidths
    });
  }, [
    columnWidths,
    dragHandlerWidthPx,
    enabled,
    fieldCount,
    pinnedLeadingFieldCount,
    showDragHandlerColumn
  ]);

  const value = useMemo<PinnedLeadingColumnsContextValue>(
    () => ({
      enabled,
      pinnedLeadingFieldCount,
      maxPinnedLeadingFieldCount,
      setPinnedLeadingFieldCount,
      layoutByFieldIndex
    }),
    [
      enabled,
      layoutByFieldIndex,
      maxPinnedLeadingFieldCount,
      pinnedLeadingFieldCount,
      setPinnedLeadingFieldCount
    ]
  );

  return (
    <PinnedLeadingColumnsContext.Provider value={value}>
      {children}
    </PinnedLeadingColumnsContext.Provider>
  );
}

export function usePinnedLeadingColumns(): PinnedLeadingColumnsContextValue {
  const ctx = useContext(PinnedLeadingColumnsContext);
  if (!ctx) {
    return {
      enabled: false,
      pinnedLeadingFieldCount: 0,
      maxPinnedLeadingFieldCount: 0,
      setPinnedLeadingFieldCount: () => {},
      layoutByFieldIndex: []
    };
  }
  return ctx;
}
